import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ProvenanceBadge } from '@/components/trust/ProvenanceBadge';
import { formatEngineVersion } from '@/lib/engineVersion';

function Row({ label, value, provenance, muted = false, strong = false }) {
  let valueTone = 'text-[#171A1D]';
  if (strong) valueTone = 'font-semibold text-[#171A1D]';
  else if (muted) valueTone = 'text-[#66707A]';
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-[#DDE1E5] last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-sm truncate ${strong ? 'font-semibold text-[#171A1D]' : 'text-[#3A4249]'}`}>
          {label}
        </span>
        {provenance && <ProvenanceBadge provenance={provenance} />}
      </div>
      <span
        className={`shrink-0 text-sm ${valueTone}`}
        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
      >
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="px-5 py-4 border-b border-[#DDE1E5] last:border-0">
      <div className="text-[11px] uppercase tracking-[0.12em] text-[#66707A] mb-2"
           style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

/** "How this estimate was calculated" — financial/analytical presentation. */
export function MethodologyPanel({ results, config, formatCost, defaultOpen = false }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const m = results.methodology;
  if (!m) return null;

  const sizeLabel = config.companySize ? t(`calcData.sizes.${config.companySize}`) : '-';
  const industryLabel = config.industry ? t(`calcData.industries.${config.industry}`) : '-';
  const assumptions = results.assumptions || [];
  const provenanceOf = (id) => assumptions.find(a => a.id === id)?.provenance;
  const dateLabel = new Date(results.calculatedAt || Date.now()).toLocaleDateString(i18n.language, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  const mult = (v) => `×${v.toFixed(2)}`;

  return (
    <section className="border border-[#DDE1E5] rounded-lg bg-white" data-testid="methodology-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#F5F6F7] transition-colors duration-150 rounded-lg"
        data-testid="methodology-toggle"
      >
        <span>
          <span className="block text-sm font-semibold text-[#171A1D]">{t('trust.methodologyHow')}</span>
          <span className="block mt-0.5 text-xs text-[#8A929B]"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            {t('trust.engineVersion')} {formatEngineVersion(m.engineVersion)} · {t('trust.calculatedAt')} {dateLabel}
          </span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[#66707A] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#66707A] shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-[#DDE1E5]" data-testid="methodology-body">
          <Section title={t('trust.sectionImplementation')}>
            <Row label={t('trust.mSize')} value={sizeLabel} provenance="USER_PROVIDED" />
            <Row label={t('trust.mBaseEffort')} value={`${m.baseEffort} d`} provenance="MODELLED" />
            <Row label={`${t('trust.mIndustry')} (${industryLabel})`} value={mult(m.industryFactor)} />
            <Row label={t('trust.mLocations')} value={mult(m.locationFactor)} />
            <Row label={t('trust.mItSystems')} value={mult(m.itSystemsFactor)} />
            <Row label={`${t('trust.mMaturity')} (L${m.maturityLevel})`} value={mult(m.maturityFactor)} />
            <Row label={t('trust.mReuse')} value={`−${m.reuseReductionPct}%`} />
            <Row label={t('trust.mFloor')} value={`${m.effortFloor} d`} muted />
            <Row label={t('trust.mEffort')} value={`${results.totalEffort} d`} strong />
          </Section>

          <Section title={t('trust.sectionLabour')}>
            <Row label={t('trust.mDelivery')} value={`${m.internalSharePct}% ${t('trust.mInternalDays').toLowerCase()}`} provenance="USER_PROVIDED" />
            <Row label={t('trust.mInternalDays')} value={`${results.internalDays} d`} />
            <Row label={t('trust.mConsultantDays')} value={`${results.consultantDays} d`} />
            <Row label={t('trust.mInternalRate')} value={formatCost(m.internalRate)} provenance={provenanceOf('internalRate')} />
            <Row label={t('trust.mConsultantRate')} value={formatCost(m.consultantRate)} provenance={provenanceOf('consultantRate')} />
            <Row label={t('results.costBreakdown')} value={formatCost(results.costBreakdown.labor)} strong />
          </Section>

          <Section title={t('trust.sectionCertification')}>
            <Row label={t('trust.mPersonnel')} value={m.effectivePersonnel} provenance={provenanceOf('effectivePersonnel')} />
            <Row label={t('trust.mAuditLadder')} value={`${m.auditLadderDays} d`} provenance="ISO_DERIVED" />
            <Row label={t('trust.mAuditAdjust')} value={mult(m.auditAdjustmentFactor)} />
            <Row label={t('trust.mSampledSites')} value={m.sampledSites} />
            <Row label={t('trust.mAuditDays')} value={`${results.auditDays} d`} strong />
            <Row label={t('trust.mCbRate')} value={formatCost(m.certBodyDayRate)} provenance="DEFAULT_ASSUMPTION" />
            <p className="mt-3 text-xs text-[#8A929B] leading-relaxed" data-testid="methodology-iso-note">
              {t('trust.mNote')}
            </p>
          </Section>

          <Section title={t('trust.sectionAdditional')}>
            <Row label={t('trust.mTraining')} value={formatCost(results.costBreakdown.training)} provenance="DEFAULT_ASSUMPTION" />
            <Row label={t('trust.mTools')} value={formatCost(results.costBreakdown.tools)} provenance="DEFAULT_ASSUMPTION" />
            <Row label={t('trust.mDuration')} value={`${results.totalDurationWeeks} ${t('estimates.weeks')}`} provenance="MODELLED" />
            <Row label={t('trust.planningRange')}
                 value={`${formatCost(results.costRange.low)} – ${formatCost(results.costRange.high)}`}
                 provenance="ESTIMATED" strong />
          </Section>
        </div>
      )}
    </section>
  );
}
